import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { TaxesEntity, TaxesFacade } from '@galabid/gb-dashboard/setup/data-access-taxes'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { TranslateService } from '@ngx-translate/core'
import * as TaxesActions from 'libs/gb-dashboard/setup/data-access-taxes/src/lib/+state/taxes.actions'
import { PendingChangesService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-pending-changes/gb-pending-changes.service'
import { of } from 'rxjs'

@UntilDestroy()
@Component({
  selector: 'gb-edit-tax-dialog',
  templateUrl: './gb-edit-tax-dialog.component.html',
  styleUrls: ['./gb-edit-tax-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GbEditTaxDialogComponent implements OnInit, OnDestroy {
  createEditLoaded$ = this.taxesFacade.createEditLoaded$

  isEditMode = false
  selectedTax$ = this.taxesFacade.selectedTaxes$
  taxMethodOptions$ = of([
    {
      name: this.translateService.instant('SETTINGS.TAXES.FORM.TAX_METHOD_ADD'),
      method: 'add',
    },
    {
      name: this.translateService.instant('SETTINGS.TAXES.FORM.TAX_METHOD_INCLUDE'),
      method: 'include',
    },
  ])

  editTaxFormFields = {
    uuid: new FormControl(''),
    label: new FormControl('', [Validators.required]),
    tax_rate: new FormControl(0, [Validators.required, Validators.min(0.5), Validators.max(99)]),
    method: new FormControl('', [Validators.required]),
    auto_apply_tax_to_items: new FormControl(false),
  }
  editTaxForm = new FormGroup(this.editTaxFormFields)

  autoApplyTaxToExistingItems = new FormControl(true)

  get taxLabel() {
    return this.editTaxForm.get('label') as FormControl
  }

  get taxPercentage() {
    return this.editTaxForm.get('tax_rate') as FormControl
  }

  get taxMethod() {
    return this.editTaxForm.get('method') as FormControl
  }

  get autoApplyTaxToItems() {
    return this.editTaxForm.get('auto_apply_tax_to_items') as FormControl
  }

  constructor(
    private taxesFacade: TaxesFacade,
    private translateService: TranslateService,
    private pendingChangesService: PendingChangesService,
  ) {}

  ngOnInit() {
    this.selectedTax$.pipe(untilDestroyed(this)).subscribe((tax) => {
      if (tax) {
        this.editTaxForm.patchValue(tax)
        this.isEditMode = true
      }
    })
    this.pendingChangesService.registerPendingChanges(this.editTaxForm, this.selectedTax$)
  }

  submit() {
    this.editTaxForm.valid ? this.save() : this.editTaxForm.markAllAsTouched()
  }

  save() {
    if (this.isEditMode) {
      this.taxesFacade.dispatch(TaxesActions.updateTax({ tax: this.editTaxForm.value as Partial<TaxesEntity> }))
    } else {
      this.taxesFacade.dispatch(
        TaxesActions.createTax({
          tax: this.editTaxForm.value as Partial<TaxesEntity>,
          applyToExistingItems: !!this.autoApplyTaxToExistingItems.value,
        }),
      )
    }
  }

  ngOnDestroy() {
    this.pendingChangesService.resetPendingChanges()
  }

  closeDialog() {
    this.pendingChangesService.closeDialogPendingChanges(GbEditTaxDialogComponent)
  }
}
